/**
 * Search, in the two steps it actually is.
 *
 *   1. identify — "C22" can name more than one product. Show the few it could be,
 *      say WHY each one matched, and let a human choose.
 *   2. cross    — the equivalents of the product that was chosen.
 *
 * Two ways to read the code, because they are two different questions:
 *
 *   Code — "what part is this?"    an article number, or a trade code like C22
 *   OE   — "what fits this OE?"    every product that cross-references it, which
 *                                  is a list by nature: the OEM part AND every
 *                                  aftermarket equivalent
 *
 * The old one-shot search did all of it at once and returned everything anyone
 * said, which buries the answer under eighty rows. It still exists on the API
 * (`/v1/search/*`) for clients built against it.
 */
import { useState } from 'react';
import { useApi } from '../lib/api';
import { useNav } from '../lib/router';
import type { ProductOe, Provenance } from '../lib/types';
import { Banner, Chip, DataBlock, Empty, ErrorBox, Loading, SourceChip, Table, Toolbar, cx } from '../ui';

type Mode = 'auto' | 'oe';

interface Candidate {
  productId: number | null;
  kind: 'observed' | 'internal';
  brand: string;
  article: string;
  name: string | null;
  sources: string[];
  identityCount: number;
  oeCount: number;
  crossCount: number;
  why: string;
}

interface IdentifyResp {
  query: { input: string; normalized: string; mode: Mode; resolvedMode: 'code' | 'oe' };
  candidates: Candidate[];
  sources: { name: string; status: string; cache?: string; error?: string }[];
  fetched: boolean;
  ingested: number;
  meta: { total: number; elapsedMs: number };
}

/** One product this cross was inherited through — a declared equivalence. */
interface Via {
  productId: number;
  brand: string;
  article: string;
}

/** A cross row is either an observed product or an injected internal one. */
interface CrossItem {
  productId?: number;
  brand?: string;
  article?: string;
  articleNumber?: string;
  name?: string | null;
  relation?: string;
  sources?: Provenance[];
  via?: Via[];
  synthetic?: boolean;
}

interface CrossResp {
  product: { id: number; brandNorm: string; articleNorm: string; name: string | null };
  cross: Record<string, CrossItem[]>;
  oes: ProductOe[];
  anticross: { oeNorm: string; status: string; reason: string | null }[];
  meta: { total: number; flagged: number; elapsedMs: number };
}

export default function Search() {
  const { request } = useApi();
  const { openProduct } = useNav();
  const [mode, setMode] = useState<Mode>('auto');
  const [brand, setBrand] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ident, setIdent] = useState<IdentifyResp | null>(null);
  const [chosen, setChosen] = useState<Candidate | null>(null);
  const [cross, setCross] = useState<CrossResp | null>(null);

  async function call<T>(path: string): Promise<T | null> {
    const r = await request<T>('GET', path);
    if (r.status >= 400) {
      const d = r.data as unknown;
      setError(
        d && typeof d === 'object' && 'error' in d ? String((d as { error: unknown }).error) : `HTTP ${r.status}`,
      );
      return null;
    }
    return r.data;
  }

  async function step1(refresh = false) {
    if (!code.trim()) return;
    setBusy(true);
    setError('');
    setChosen(null);
    setCross(null);
    const q = new URLSearchParams({ q: code.trim(), mode });
    if (brand.trim()) q.set('brand', brand.trim());
    if (refresh) q.set('refresh', '1');
    const r = await call<IdentifyResp>(`/v1/identify?${q.toString()}`);
    setIdent(r);
    setBusy(false);
    // One unambiguous answer needs no choosing.
    const only = r?.candidates.length === 1 ? r.candidates[0] : undefined;
    if (only) void step2(only);
  }

  /**
   * Cross the chosen candidate.
   *
   * A candidate normally carries its product id — identify folds every upstream
   * answer into the graph before replying, so even a code seen for the first time
   * is a real product by the time you see it. The resolve fallback covers the one
   * case that can still miss: the graph learned the code under a brand we could not
   * match. Better one extra request than a button that does nothing.
   */
  async function step2(c: Candidate) {
    setBusy(true);
    setError('');
    setChosen(c);

    let id = c.productId;
    if (id === null) {
      const r = await call<{ id: number }>(
        `/v1/products/resolve?brand=${encodeURIComponent(c.brand)}&article=${encodeURIComponent(c.article)}`,
      );
      if (!r) {
        setBusy(false);
        return;
      }
      id = r.id;
    }

    setCross(await call<CrossResp>(`/v1/products/${id}/cross`));
    setBusy(false);
  }

  const groups = cross ? Object.entries(cross.cross) : [];

  return (
    <>
      <Banner icon="⌕">
        <b>Code</b> asks <i>which product is this?</i> — an article number, or a trade code like{' '}
        <span className="mono">C22</span>. <b>OE</b> asks <i>what fits this OE?</i> and answers with every product that
        cross-references it. Either way, step one names the product and step two crosses it.
      </Banner>

      <Toolbar>
        <div className="flex rounded border border-line">
          {(['auto', 'oe'] as const).map((m) => (
            <button
              key={m}
              className={cx(
                'px-3 py-1 text-[12.5px] first:rounded-l last:rounded-r',
                mode === m ? 'bg-accent text-white' : 'text-muted hover:text-fg',
              )}
              onClick={() => setMode(m)}
              title={m === 'auto' ? 'Read it as a part code (falls back to OE)' : 'Read it as an OE number'}
            >
              {m === 'auto' ? 'Code' : 'OE'}
            </button>
          ))}
        </div>
        <input
          className="input mono"
          style={{ width: 150 }}
          placeholder="Brand (optional)"
          value={brand}
          disabled={mode === 'oe'}
          onChange={(e) => setBrand(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && step1()}
        />
        <input
          className="input mono"
          style={{ flex: 1, minWidth: 200 }}
          placeholder={mode === 'oe' ? 'OE number…' : 'Code…'}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && step1()}
          autoFocus
        />
        <button className="btn btn-primary" disabled={!code.trim() || busy} onClick={() => step1()}>
          Identify
        </button>
        {ident && (
          <button
            className="btn"
            disabled={busy}
            onClick={() => step1(true)}
            title="Ignore the cache and ask the catalogs again"
          >
            Refresh
          </button>
        )}
      </Toolbar>

      {error && <ErrorBox>{error}</ErrorBox>}

      {ident && (
        <div className="mb-3 text-[11.5px] text-faint">
          {ident.fetched
            ? `Asked the catalogs — the graph did not know this code yet${
                ident.ingested ? `, and learned ${ident.ingested} response${ident.ingested === 1 ? '' : 's'} from the answer` : ''
              }.`
            : 'Answered from the product graph, with no upstream call.'}{' '}
          {ident.query.resolvedMode === 'oe' && ident.query.mode === 'auto' && (
            <span className="text-muted">No part carries that code, so it was read as an OE. </span>
          )}
          {ident.meta.elapsedMs} ms
          {ident.sources.map((s, i) => (
            <span key={`${s.name}-${i}`} className={cx('ml-2', s.status === 'ok' ? 'text-muted' : 'text-bad')}>
              {s.name}
              {s.cache ? ` (${s.cache})` : ''}
            </span>
          ))}
        </div>
      )}

      {/* Step 1 — which product is it? */}
      {ident && !chosen && (
        <DataBlock
          title={ident.query.resolvedMode === 'oe' ? 'Which product do you want to cross?' : 'Which product do you mean?'}
          sub={`${ident.candidates.length} candidate${ident.candidates.length === 1 ? '' : 's'} for “${ident.query.input}”`}
          raw={ident}
        >
          {ident.candidates.length === 0 ? (
            <Empty>
              {ident.query.resolvedMode === 'oe'
                ? 'No product cross-references that OE.'
                : 'No product matches that code.'}
            </Empty>
          ) : (
            <div className="flex flex-col gap-2 p-3">
              {ident.candidates.map((c, i) => (
                <button
                  key={c.productId ?? `new-${i}`}
                  className="blk cursor-pointer p-3 text-left hover:border-accent"
                  onClick={() => step2(c)}
                >
                  <div className="flex items-center gap-2">
                    <span className="mono text-[14px] font-semibold">
                      {c.brand} {c.article}
                    </span>
                    {c.kind === 'internal' && <Chip className="border-accent text-accent">internal</Chip>}
                    {c.identityCount > 1 && <Chip className="border-line2 text-muted">{c.identityCount} codes</Chip>}
                    <div className="ml-auto flex gap-1">
                      {c.sources.map((s) => (
                        <SourceChip key={s} source={s} />
                      ))}
                    </div>
                  </div>
                  {c.name && <div className="mt-0.5 text-[12.5px] text-muted">{c.name}</div>}
                  <div className="mt-1 text-[11.5px] text-faint">
                    {c.why} · {c.oeCount} OE{c.oeCount === 1 ? '' : 's'} · {c.crossCount} cross
                  </div>
                </button>
              ))}
            </div>
          )}
        </DataBlock>
      )}

      {/* Step 2 — the cross of that product. */}
      {chosen && (
        <>
          <Toolbar>
            <button className="btn btn-sm" onClick={() => setChosen(null)}>
              ← Other candidates
            </button>
            <span className="mono text-[13px] font-semibold">
              {chosen.brand} {chosen.article}
            </span>
            {chosen.name && <span className="text-[12.5px] text-muted">{chosen.name}</span>}
            <div className="flex-1" />
            {cross && (
              <button className="btn btn-sm" onClick={() => openProduct(cross.product.id)}>
                Open full product
              </button>
            )}
          </Toolbar>

          {busy && <Loading />}

          {!busy && cross && (
            <>
              {cross.anticross.length > 0 && (
                <Banner icon="⚠">
                  Anticross-flagged — a match on {cross.anticross.map((a) => a.oeNorm).join(', ')} is known to be wrong.
                </Banner>
              )}

              {groups.length === 0 ? (
                <Empty>No cross references known for this product.</Empty>
              ) : (
                groups.map(([source, items]) => (
                  <div key={source} className="mb-3.5">
                    <DataBlock
                      title={
                        <span className="flex items-center gap-2">
                          <SourceChip source={source} />
                          <span className="font-normal text-muted">says these are equivalent</span>
                        </span>
                      }
                      sub={`${items.length}`}
                      raw={items}
                    >
                      <Table cols={['Brand', 'Article', 'Name', 'Relation', 'Via', '']}>
                        {items.map((x, i) => (
                          <tr key={x.productId ?? i}>
                            <td className="mono">{x.brand ?? '—'}</td>
                            <td className="mono">{x.article ?? x.articleNumber ?? '—'}</td>
                            <td>
                              {x.name ?? '—'}
                              {x.synthetic && <Chip className="ml-1.5 border-accent text-accent">internal</Chip>}
                            </td>
                            <td className="text-muted">{x.relation ?? 'interchange'}</td>
                            <td className="mono text-[11.5px] text-faint">
                              {x.via?.length
                                ? x.via.map((v) => `${v.brand} ${v.article}`).join(', ')
                                : '—'}
                            </td>
                            <td>
                              {x.productId !== undefined && (
                                <button className="btn btn-sm" onClick={() => openProduct(x.productId!)}>
                                  Open
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </Table>
                    </DataBlock>
                  </div>
                ))
              )}

              <DataBlock title="OE codes" sub={`${cross.oes.length}`} raw={cross.oes}>
                {cross.oes.length === 0 ? (
                  <Empty>No OE codes.</Empty>
                ) : (
                  <Table cols={['OE code', 'Stated by']}>
                    {cross.oes.map((o) => (
                      <tr key={o.code}>
                        <td className="mono">{o.code}</td>
                        <td>
                          <div className="flex gap-1">
                            {[...new Set(o.sources.map((s) => s.source))].map((s) => (
                              <SourceChip key={s} source={s} />
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </Table>
                )}
              </DataBlock>
            </>
          )}
        </>
      )}

      {!ident && !busy && <Empty>Type a code above to begin.</Empty>}
      {busy && !chosen && <Loading />}
    </>
  );
}
